import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useT } from '@/lib/i18n';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import {
  approvalState,
  errorText,
  isClientRole,
  useDocumentApprovals,
  useOrgRole,
  type Approval,
} from '@/lib/portal';
import { cn } from '@/lib/utils';
import { ApprovalActions } from './approval-actions';
import { DecisionDialog } from './decision-dialog';

/**
 * Approval state of a draft invoice or bill, on the document (document 06, "Client portal"):
 * who asked, who decided and their comment; Approve, Reject, Withdraw; and, for Outss staff,
 * "Ask the client to approve".
 */
export function ApprovalStrip({
  documentId,
  kind,
  status,
}: {
  documentId: string;
  kind: string;
  status: string;
}) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const role = useOrgRole();
  const list = useDocumentApprovals(documentId);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = useT();
  const ask = useMutation({
    mutationFn: (note: string) =>
      api<Approval>(`/orgs/${orgId}/approvals`, {
        method: 'POST',
        body: JSON.stringify({ documentId, ...(note ? { note } : {}) }),
      }),
    onSuccess: async () => {
      setAsking(false);
      setError(null);
      toast(t('approvals.sentToClient'));
      await qc.invalidateQueries();
    },
    onError: (e) => setError(errorText(e)),
  });
  if (kind !== 'invoice' && kind !== 'bill') return null;
  const latest = list.data?.[0];
  const staff = role !== null && !isClientRole(role);
  const canAsk =
    staff && status === 'draft' && (!latest || latest.status !== 'pending') && !latest?.readyToPost;
  if (!latest && !canAsk) return null;
  const tone = !latest
    ? 'muted'
    : latest.status === 'rejected'
      ? 'bad'
      : latest.status === 'pending'
        ? 'wait'
        : 'muted';
  return (
    <div
      data-testid="approval-strip"
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm',
        tone === 'bad' && 'border-destructive/30 bg-destructive/5',
        tone === 'wait' && 'border-amber-300 bg-amber-50 dark:bg-amber-950/30',
        tone === 'muted' && 'bg-muted/40',
      )}
    >
      <div className="min-w-0">
        {latest ? (
          <>
            <span className="font-medium">{approvalState(latest)}</span>
            <span className="text-muted-foreground">
              {' · '}
              {t(latest.direction === 'to_client' ? 'approvals.askedBy' : 'approvals.submittedBy', {
                who: latest.requestedBy ?? t('files.someone'),
                date: formatDateTime(latest.requestedAt),
              })}
              {latest.decidedBy && latest.decidedAt
                ? ` · ${t('approvals.decidedBy', {
                    status: t(`approvals.status_${latest.status}`),
                    who: latest.decidedBy,
                    date: formatDateTime(latest.decidedAt),
                  })}`
                : ''}
            </span>
            {latest.note ? (
              <div className="text-muted-foreground">
                {t('approvals.note', { text: latest.note })}
              </div>
            ) : null}
            {latest.decisionComment ? (
              <div className={latest.status === 'rejected' ? 'text-destructive' : ''}>
                {t('approvals.commentText', { text: latest.decisionComment })}
              </div>
            ) : null}
            {latest.status === 'rejected' &&
            latest.direction === 'to_staff' &&
            role === 'client_owner' ? (
              <div className="text-muted-foreground">{t('approvals.changeAndResend')}</div>
            ) : null}
          </>
        ) : (
          <span className="text-muted-foreground">
            {kind === 'invoice' ? t('approvals.wantClientInvoice') : t('approvals.wantClientBill')}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {latest ? <ApprovalActions a={latest} /> : null}
        {canAsk ? (
          <Button size="sm" variant="outline" onClick={() => setAsking(true)}>
            {t('approvals.askClient')}
          </Button>
        ) : null}
        <Button size="sm" variant="link" asChild>
          <Link to="/documents/approvals">{t('approvals.title')}</Link>
        </Button>
      </div>
      <DecisionDialog
        open={asking}
        title={kind === 'invoice' ? t('approvals.askTitleInvoice') : t('approvals.askTitleBill')}
        description={t('approvals.askDescription')}
        confirm={t('approvals.sendForApproval')}
        destructive={false}
        label={t('approvals.noteToClient')}
        pending={ask.isPending}
        error={error}
        onConfirm={(note) => ask.mutate(note)}
        onClose={() => {
          setAsking(false);
          setError(null);
        }}
      />
    </div>
  );
}
